/**
 * "Promote to case" — writes the last Targeted Node Intelligence run to the
 * case spine through POST /cases/{id}/promote (`source: orchestrator`; the
 * server builds the Flow IR from the run's traces, W3-06).
 *
 * A run streamed with the case id is already promoted by the backend when it
 * completes (`complete.spine_promotion`); that outcome is shown here too, and
 * the button promotes again (idempotent on the spine) or fills the gap when the
 * run came from the per-section fallback or a saved snapshot.
 */
import React, { useEffect, useState } from 'react';
import { GitMerge, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { casePromoteService, type CasePromoteResult } from '../../services/api';
import { useFeature } from '../../auth/features';
import { useCaseSpineStore } from '../../stores/useCaseSpineStore';
import type { SpinePromotion } from '../../hooks/useAutoInvestigation';
import { isUuid, promotionSummary } from './caseWallets';

export interface PromoteToCaseControlProps {
    caseId: string | null | undefined;
    investigationId?: string | null;
    /** Chain and subject wallet of the run being promoted. */
    chain: string | null | undefined;
    subject: string | null | undefined;
    isRunning: boolean;
    isComplete: boolean;
    spinePromotion?: SpinePromotion | null;
    getResults: () => Record<string, any> | null;
}

type PromoteState =
    | { status: 'idle' }
    | { status: 'promoting' }
    | { status: 'done'; result: CasePromoteResult }
    | { status: 'error'; message: string };

export const NO_CASE_REASON = 'Open the Workbench from a case (Cases → open a case → Workbench) to promote a run to it.';

/** Why the control is disabled, or null when it can run. */
export function promoteDisabledReason(p: {
    caseId: string | null | undefined;
    canWriteCases: boolean;
    isRunning: boolean;
    isComplete: boolean;
    hasResults: boolean;
    chain: string | null | undefined;
    subject: string | null | undefined;
}): string | null {
    if (!p.caseId) return NO_CASE_REASON;
    if (!isUuid(p.caseId)) return 'This case link has no case-graph id, so a run cannot be promoted to it.';
    if (!p.canWriteCases) return 'Your access does not include writing to cases.';
    if (p.isRunning) return 'Wait for the run to finish.';
    if (!p.isComplete || !p.hasResults) return 'Run Targeted Node Intelligence first: its traces are what is promoted.';
    if (!p.chain || !p.subject) return 'The run has no subject wallet and chain to promote.';
    return null;
}

function errorText(e: any): string {
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string' && detail) return detail;
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
    return e?.message || 'promotion failed';
}

export const PromoteToCaseControl: React.FC<PromoteToCaseControlProps> = ({
    caseId, investigationId, chain, subject, isRunning, isComplete, spinePromotion, getResults,
}) => {
    const canWriteCases = useFeature('cases.own');
    const [state, setState] = useState<PromoteState>({ status: 'idle' });
    const bump = useCaseSpineStore((s) => s.bump);

    // A new run (or a different case) makes an earlier manual result stale.
    useEffect(() => { setState({ status: 'idle' }); }, [caseId, subject, isRunning]);

    // The backend promoted the streamed run: the case's wallet list re-reads.
    useEffect(() => {
        if (caseId && spinePromotion?.status === 'ok') bump(caseId);
    }, [caseId, spinePromotion, bump]);

    const results = isComplete && !isRunning ? getResults() : null;
    const reason = promoteDisabledReason({
        caseId, canWriteCases, isRunning, isComplete, hasResults: !!results, chain, subject,
    });
    const busy = state.status === 'promoting';

    const onPromote = async () => {
        if (reason || busy || !caseId || !results || !chain || !subject) return;
        setState({ status: 'promoting' });
        try {
            const result = await casePromoteService.promote(caseId, {
                source: 'orchestrator',
                results,
                chain,
                subject,
                ...(isUuid(investigationId) ? { investigation_id: investigationId } : {}),
            });
            setState({ status: 'done', result });
            bump(caseId);
            toast.success(`Promoted to case: ${promotionSummary(result)}`);
        } catch (e: any) {
            const message = errorText(e);
            setState({ status: 'error', message });
            toast.error(`Promote to case failed: ${message}`);
        }
    };

    const skipped = state.status === 'done' && Array.isArray(state.result.skipped) ? state.result.skipped : [];

    return (
        <div className="space-y-1 pt-1" data-testid="wb-promote-to-case">
            <button
                type="button"
                onClick={onPromote}
                disabled={!!reason || busy}
                aria-describedby={reason ? 'wb-promote-reason' : undefined}
                data-tour="wb-promote-to-case"
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/80 text-foreground border border-border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5 shrink-0" />}
                {busy ? 'Promoting…' : 'Promote to case'}
            </button>
            {reason && (
                <p id="wb-promote-reason" className="text-[10px] text-muted-foreground" data-testid="wb-promote-reason">{reason}</p>
            )}
            {spinePromotion?.status === 'ok' && state.status !== 'done' && (
                <p className="text-[10px] text-emerald-700 dark:text-emerald-300" data-testid="wb-promote-auto">
                    Promoted when the run completed: {promotionSummary({
                        nodes_written: spinePromotion.nodes_written ?? 0,
                        edges_written: spinePromotion.edges_written ?? 0,
                        transfers_written: spinePromotion.transfers_written ?? 0,
                        skipped: spinePromotion.skipped ?? [],
                        flow_nodes: spinePromotion.flow_nodes ?? 0,
                        flow_edges: spinePromotion.flow_edges ?? 0,
                    })}
                </p>
            )}
            {spinePromotion?.status === 'error' && state.status !== 'done' && (
                <p className="text-[10px] text-amber-700 dark:text-amber-400" role="alert" data-testid="wb-promote-auto-error">
                    The automatic promotion at the end of the run failed ({spinePromotion.error || 'no detail'}). Promote to case retries it.
                </p>
            )}
            {state.status === 'done' && (
                <div className="text-[10px] text-emerald-700 dark:text-emerald-300" data-testid="wb-promote-result">
                    {promotionSummary(state.result)}
                    {skipped.length > 0 && (
                        <ul className="mt-0.5 list-disc pl-4 text-muted-foreground">
                            {skipped.slice(0, 5).map((s, i) => (
                                <li key={`${s.ref || ''}-${i}`}>{s.ref || 'element'}: {s.reason || 'refused'}</li>
                            ))}
                            {skipped.length > 5 && <li>and {skipped.length - 5} more</li>}
                        </ul>
                    )}
                </div>
            )}
            {state.status === 'error' && (
                <p className="text-[10px] text-red-600 dark:text-red-400" role="alert" data-testid="wb-promote-error">
                    Promote to case failed: {state.message}
                </p>
            )}
        </div>
    );
};
